import jwt_decode from "jwt-decode";

const localStorageKey = "__timed_user_token__";
const localStorageKeyR = "__timed_user_refreshtoken__";

let refreshingPromise: Promise<string> | undefined = undefined;

const API_BASE_URL_ROOT = process.env.REACT_APP_API_URL_ROOT;
const nonSessionEndpoints = ["monitoring_navigations"];

function isNonSessionEndpoint(endpoint: string) {
    return nonSessionEndpoints.some((nonSessionEndpoint) => endpoint.startsWith(nonSessionEndpoint));
}

function logAuthDiagnostic(step: string, details: string, extra: Record<string, unknown> = {}) {
    void import("../diagRuntime")
        .then(({ logRuntimeDiagnostic }) => logRuntimeDiagnostic(`auth-${step}`, details, extra))
        .catch((error) => console.warn("[auth] failed to load diagnostics", error));
}

function getStoredToken(key: string) {
    try {
        return window.localStorage.getItem(key);
    } catch (error) {
        console.warn("[auth] localStorage unavailable", error);
        return null;
    }
}

const refreshToken = async (): Promise<string>=>{
    const refresh_token = getStoredToken(localStorageKeyR);
    if(!refresh_token){
        removeToken("refresh-token-missing");
        throw new Error("refresh token missing");
    }
    const body = {refresh_token}
    let response = await fetch(`${process.env.REACT_APP_API_URL}/token/refresh`, {
        method:'POST',
        headers:{ "content-type": "application/json" },
        body: JSON.stringify(body)
    })
    let data: any = null;
    try {
        data = await response.json();
    } catch (error) {
        console.warn("[auth] invalid refresh response", error);
    }

    if(!response.ok || !data?.token || !data?.refresh_token){
        removeToken("refresh-token-invalid");
        throw new Error("refresh token invalid");
    }
    const nextToken = String(data.token);
    const nextRefreshToken = String(data.refresh_token);

    try {
        localStorage.setItem("__timed_user_token__", nextToken);
        localStorage.setItem("__timed_user_refreshtoken__", nextRefreshToken);
    } catch (error) {
        console.warn("[auth] failed to persist refreshed token", error);
        removeToken("refresh-token-storage-failed");
        throw new Error("refresh token storage failed");
    }

    return nextToken;
}

function refreshTokenOnce(): Promise<string> {
    if (!refreshingPromise) {
        refreshingPromise = refreshToken().finally(() => {
            refreshingPromise = undefined;
        });
    }

    return refreshingPromise;
}

export async function fileFetcher<T>(endpoint:string, fileName:string):Promise<T|void>{
    const token = getStoredToken(localStorageKey);

    const headers: any = { Authorization: `Bearer ${token}` };

    const response = await fetch(`${API_BASE_URL_ROOT}${endpoint}`, { method: "GET", headers });

    if (!response.ok) {
        throw new Error("Failed to download file");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export async function simpleFetcher<T>(endpoint:string, { body, ...customConfig }: any = {}, isNoContentType:boolean = false):Promise<T | never>{
    const headers: any = { "content-type": "application/json" };
    const config = {
        method: body ? "POST" : "GET",
        ...customConfig,
        headers: {
            ...headers,
            ...customConfig.headers,
        },
    };
    if (body) {
        config.body = !isNoContentType ? JSON.stringify(body) : body;

    }
    return window.fetch(`${process.env.REACT_APP_API_URL_ROOT}/${endpoint}`, config).then(async (response)=>{
        const code = response.status;
        const result = await response.json();
        if(code<400){
            return result;
        } else {
            throw new Error(result.message);
        }
    })
}

export async function dataFetcher<T>(endpoint: string, { body, ...customConfig }: any = {}, isNoContentType:boolean = false): Promise<T | never> {
    let token = getStoredToken(localStorageKey);
    const headers: any = !isNoContentType ? { "content-type": "application/json" } : {};
    if (token) {
        let jwtDecode:any;
        try {
            jwtDecode = jwt_decode(token);
        } catch (error) {
            console.warn("[auth] invalid stored token", error);
            removeToken("stored-token-invalid");
            throw new Error("invalid stored token");
        }
        const exp =jwtDecode['exp'];
        const Today = new Date();
        const timeToday = Today.getTime();
        if(timeToday/1000>exp){
            token = await refreshTokenOnce();
        }
        headers.Authorization = `Bearer ${token}`;
    }
    const config = {
        method: body ? "POST" : "GET",
        ...customConfig,
        headers: {
            ...headers,
            ...customConfig.headers,
        },
    };
    if (body) {
        config.body = !isNoContentType ? JSON.stringify(body) : body;

    }
    return window.fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, config).then(async (response) => {
        if (response.status === 401) {
            if (endpoint === "login") {
                throw new Error("401Error");
            }
            if (isNonSessionEndpoint(endpoint)) {
                throw new Error(`401:${endpoint}`);
            }
            removeToken(`401:${endpoint}`);
            throw new Error("pas autorisé");
        }
        if(response.status === 403){
            console.log('Error fetch')
        }
        if (response.ok) {
            if (response.status === 204) {
                return;
            }
            return await response.json();
        } else {
            const errorMessage = await response.text();
            const errorCode = response.status;
            throw new Error(errorCode + errorMessage);
        }
    });
}

export function removeToken(reason: string = "manual") {

    let paramsF = window.location.search;
    let params = "";
    if(paramsF.length>1){
        params = paramsF;
    }
    try {
        localStorage.setItem("q", JSON.stringify(params));
        window.localStorage.removeItem(localStorageKey);
        window.localStorage.removeItem(localStorageKeyR);
    } catch (error) {
        console.warn("[auth] failed to clear local storage", error);
    }

    if (window.location.pathname !== "/login") {
        logAuthDiagnostic("redirect-login", reason, {
            reason,
            from: window.location.pathname,
            hasSearch: window.location.search.length > 0,
        });
        window.location.replace("/login");
    } else {
        logAuthDiagnostic("already-login", reason, { reason });
    }
}
